/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import {
    ChoiceGroup,
    IChoiceGroupOption,
    IChoiceGroupOptionStyleProps,
    IChoiceGroupOptionStyles,
    IChoiceGroupProps,
    IChoiceGroupStyles
} from '@fluentui/react/lib/ChoiceGroup';
import { Icon } from '@fluentui/react/lib/Icon';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { ITextFieldProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { DataSplittingModes } from 'src/modules/modelTraining/types/trainingTypes';
import { PalettePivotType } from 'src/verticals/EntityPalette';

export type ControlDatasetOptionsProps = {
    tagDataLink: string;
    testingPercentage: number;
    dismissModal?: () => void;
    trainingPercentage: number;
    arePercentagesValid: boolean;
    userDefinedTestingPercentage?: number;
    userDefinedTrainingPercentage?: number;
    isUserDefinedSplittingEnabled: boolean;
    dataSplittingOption: DataSplittingModes;
    userDefinedOptionDisabledTooltip: string;
    onChangeTestingPercentage: (value: string) => void;
    onChangeDataSplittingOption: (opt: string) => void;
    onChangeTrainingPercentage: (value: string) => void;
};

const messages = defineMessages({
    testing: { id: 'modelTraining.controlDatasetOptions.testing', defaultMessage: 'testing' },
    tagging: { id: 'modelTraining.controlDatasetOptions.tagData', defaultMessage: 'Data Labeling' },
    training: { id: 'modelTraining.controlDatasetOptions.training', defaultMessage: 'training' },
    percentageFor: { id: 'modelTraining.controlDatasetOptions.percentageFor', defaultMessage: '% for' },
    currentSplit: { id: 'modelTraining.controlDatasetOptions.currentSplit', defaultMessage: 'Your current split of data' },
    userDefinedOptionTitle: {
        id: 'modelTraining.controlDatasetOptions.userDefinedOptionTitle',
        defaultMessage: 'Use a manual split of training and testing data'
    },
    randomDataSplitOptionTitle: {
        id: 'modelTraining.controlDatasetOptions.randomDataSplitOptionTitle',
        defaultMessage: 'Automatically split the testing set from training data'
    },
    invalidPercentage: {
        id: 'modelTraining.controlDatasetOptions.invalidPercentage',
        defaultMessage: 'The values cannot be empty and you can only use numbers from 0 to 100.'
    },
    userDefinedOptionDescription: {
        id: 'modelTraining.controlDatasetOptions.userDefinedOptionDescription',
        defaultMessage: 'We’ll use the training and testing sets that you’ve assigned in'
    },
    userDefinedOptionDescription2: {
        id: 'modelTraining.controlDatasetOptions.userDefinedOptionDescription2',
        defaultMessage: 'to create your custom model and measure its performance.'
    },
    userDefinedOptionDataDistribution: {
        id: 'modelTraining.controlDatasetOptions.userDefinedOptionDataDistribution',
        defaultMessage: 'View distribution of data'
    },
    randomDataSplitOptionDescription: {
        id: 'modelTraining.controlDatasetOptions.randomDataSplitOptionDescription',
        defaultMessage: `We’ll select stratified samples from all training data according to the percentages that you provide here. Any data already assigned to the testing set will be ignored completely.`
    }
});

const scopedTheme = getScopedTheme('ControlDatasetOptions');
const { styleComponent } = getStylistV2('ControlDatasetOptions');
const CardWrapper = styleComponent<IStackProps>(Stack)(
    'CardWrapper',
    (theme, cssVars) => ({
        color: `${cssVars.color}`,
        boxShadow: theme.boxShadow,
        border: `1px solid ${cssVars.borderColor}`
    }),
    {
        cssVars: { borderColor: '', color: '' },
        cssVarsSetter: ({ isSelected, isCardDisabled }: { isSelected: boolean; isCardDisabled: boolean }) => ({
            borderColor: isSelected ? scopedTheme.selectedBorderColor : '',
            color: isCardDisabled ? scopedTheme.disabledCardWrapperFontColor : ''
        })
    }
);

const horizontalChoiceGroupStyles = classNamesFunction<IChoiceGroupProps, IChoiceGroupStyles>()({ flexContainer: { display: 'flex' } });
const commonPercentageTextFieldStyles = classNamesFunction<ITextFieldProps, ITextFieldStyles>()({ fieldGroup: { width: 52, height: 26 } });
const invalidPercentageTextFieldBorderStyles = classNamesFunction<ITextFieldProps, ITextFieldStyles>()({
    fieldGroup: { border: `1px solid ${scopedTheme.invalidInputBorderColor}` }
});
const percentageChoiceGroupOptionStyles = classNamesFunction<IChoiceGroupOptionStyleProps, IChoiceGroupOptionStyles>()({
    root: { marginRight: 20 }
});
const errorMessageStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: { flex: 1, color: scopedTheme.invalidInputBorderColor, marginTop: 0 }
});
const choiceGroupOptionStyles = classNamesFunction<IChoiceGroupOptionStyleProps, IChoiceGroupOptionStyles>()({
    input: { zIndex: -1 },
    root: { fontWeight: 600, flex: 1 },
    choiceFieldWrapper: { height: '100%' }
});

export const ControlDatasetOptions = observer((props: ControlDatasetOptionsProps) => {
    const {
        tagDataLink,
        testingPercentage,
        trainingPercentage,
        dismissModal = noop,
        dataSplittingOption,
        arePercentagesValid,
        onChangeTestingPercentage,
        onChangeTrainingPercentage,
        onChangeDataSplittingOption,
        userDefinedTestingPercentage,
        userDefinedTrainingPercentage,
        isUserDefinedSplittingEnabled,
        userDefinedOptionDisabledTooltip
    } = props;
    const history = useHistory();
    const { localize } = useLocalization();
    const { isMediumOrGreater } = useResponsiveMode();
    const { rootStore } = useGlobalState();
    const isAmlConnected = !!rootStore.currentCustomTextProjectStore?.amlProjectPath;

    const percentageTextFieldStyles = mergeStyleSets(
        commonPercentageTextFieldStyles,
        !arePercentagesValid && invalidPercentageTextFieldBorderStyles
    );

    const navigateToTaggingPage = (openedPivot?: PalettePivotType) => {
        dismissModal();
        history.push(tagDataLink, openedPivot);
    };

    const options: IChoiceGroupOption[] = [
        {
            key: 'percentage',
            text: localize(messages.randomDataSplitOptionTitle),
            styles: mergeStyleSets(choiceGroupOptionStyles, percentageChoiceGroupOptionStyles),
            onRenderField: (fieldProps, defaultRenderer) => (
                <CardWrapper
                    tokens={{ childrenGap: 8, padding: '12px 8px' }}
                    customProps={{ isSelected: fieldProps.key === dataSplittingOption, isCardDisabled: false }}>
                    {defaultRenderer(fieldProps)}

                    <Stack tokens={{ childrenGap: 42 }}>
                        <LuisText>{localize(messages.randomDataSplitOptionDescription)}</LuisText>

                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                            <Stack tokens={{ childrenGap: 8 }}>
                                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                    <LuisTextField
                                        placeholder="0-100"
                                        styles={percentageTextFieldStyles}
                                        value={`${trainingPercentage ?? ''}`}
                                        disabled={fieldProps.key !== dataSplittingOption}
                                        onChange={(_, newValue) => onChangeTrainingPercentage(newValue)}
                                        ariaLabel={`${localize(messages.percentageFor)} ${localize(messages.training)}`}
                                    />
                                    <LuisText>
                                        {localize(messages.percentageFor)}&nbsp;{localize(messages.training)}
                                    </LuisText>
                                </Stack>

                                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                                    <LuisTextField
                                        placeholder="0-100"
                                        styles={percentageTextFieldStyles}
                                        value={`${testingPercentage ?? ''}`}
                                        disabled={fieldProps.key !== dataSplittingOption}
                                        onChange={(_, newValue) => onChangeTestingPercentage(newValue)}
                                        ariaLabel={`${localize(messages.percentageFor)} ${localize(messages.testing)}`}
                                    />
                                    <LuisText>
                                        {localize(messages.percentageFor)}&nbsp;{localize(messages.testing)}
                                    </LuisText>
                                </Stack>
                            </Stack>

                            {!arePercentagesValid && (
                                <LuisText styles={errorMessageStyles} variant="small" role="alert">
                                    <Icon iconName="ErrorBadge" styles={{ root: { color: scopedTheme.invalidInputBorderColor } }} />
                                    &nbsp;
                                    {localize(messages.invalidPercentage)}
                                </LuisText>
                            )}
                        </Stack>
                    </Stack>
                </CardWrapper>
            )
        },
        {
            key: 'manual',
            styles: choiceGroupOptionStyles,
            disabled: isAmlConnected || !isUserDefinedSplittingEnabled,
            text: localize(messages.userDefinedOptionTitle),
            onRenderField: (fieldProps, defaultRenderer) => (
                <LuisTooltip hidden={isAmlConnected || isUserDefinedSplittingEnabled} content={userDefinedOptionDisabledTooltip}>
                    <CardWrapper
                        verticalFill
                        tokens={{ childrenGap: 8, padding: '12px 8px' }}
                        customProps={{
                            isSelected: fieldProps.key === dataSplittingOption,
                            isCardDisabled: !isUserDefinedSplittingEnabled
                        }}>
                        {defaultRenderer(fieldProps)}
                        <LuisText>
                            {localize(messages.userDefinedOptionDescription)}&nbsp;
                            <Link
                                as="a"
                                underline
                                tabIndex={0}
                                styles={{ root: { zIndex: -1 } }}
                                onClick={() => navigateToTaggingPage()}
                                disabled={!isUserDefinedSplittingEnabled}
                                onKeyDown={e => {
                                    if (e.key === LuisKeyCodes.Enter) {
                                        navigateToTaggingPage();
                                    }
                                }}>
                                {localize(messages.tagging)}
                            </Link>
                            &nbsp;
                            {localize(messages.userDefinedOptionDescription2)}
                        </LuisText>
                        <LuisText fontWeight="bold">{localize(messages.currentSplit)}</LuisText>
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <LuisText>
                                {userDefinedTrainingPercentage != null ? userDefinedTrainingPercentage : '-'}%&nbsp;
                                {localize(messages.training)}
                            </LuisText>
                            <LuisText>
                                {userDefinedTestingPercentage != null ? userDefinedTestingPercentage : '-'}%&nbsp;
                                {localize(messages.testing)}
                            </LuisText>
                        </Stack>
                        <LuisText>
                            <Link
                                as="a"
                                underline
                                tabIndex={0}
                                disabled={!isUserDefinedSplittingEnabled}
                                onClick={() => navigateToTaggingPage('distribution')}
                                onKeyDown={e => {
                                    if (e.key === LuisKeyCodes.Enter) {
                                        navigateToTaggingPage('distribution');
                                    }
                                }}>
                                {localize(messages.userDefinedOptionDataDistribution)}
                            </Link>
                        </LuisText>
                    </CardWrapper>
                </LuisTooltip>
            )
        }
    ];

    return (
        <ChoiceGroup
            options={options}
            selectedKey={dataSplittingOption}
            styles={isMediumOrGreater && horizontalChoiceGroupStyles}
            onChange={(_, option) => onChangeDataSplittingOption(option.key as DataSplittingModes)}
        />
    );
});
